import type { PatternRequest, ShotPattern } from './ShotPattern';

/** Fail visibly to single-flight results; never block the fitting or run a
 * second physics world on the rendering thread. No customer data leaves here. */
export class PatternRunner {
  private worker: Worker | null = null;
  private pending = new Map<string, { resolve: (pattern: ShotPattern | null) => void; timer: ReturnType<typeof setTimeout> }>();
  constructor() {
    if (typeof Worker === 'undefined') return;
    try {
      this.worker = new Worker(new URL('./paired.worker.ts', import.meta.url), { type: 'module' });
      this.worker.onmessage = ({ data }: MessageEvent<{ id: string; pattern: ShotPattern | null }>) => {
        const task = this.pending.get(data.id);
        if (!task) return;
        clearTimeout(task.timer);
        this.pending.delete(data.id);
        task.resolve(data.pattern);
      };
      this.worker.onerror = () => this.dispose();
      this.worker.onmessageerror = () => this.dispose();
    } catch { this.worker = null; }
  }
  get available(): boolean { return this.worker !== null; }
  run(request: PatternRequest): Promise<ShotPattern | null> {
    if (!this.worker) return Promise.resolve(null);
    return new Promise(resolve => {
      const timer = setTimeout(() => this.dispose(), 60000);
      this.pending.set(request.id, { resolve, timer });
      try { this.worker!.postMessage(request); } catch { this.dispose(); }
    });
  }
  dispose(): void {
    this.worker?.terminate();
    this.worker = null;
    for (const task of this.pending.values()) { clearTimeout(task.timer); task.resolve(null); }
    this.pending.clear();
  }
}
